import React, { useState, useEffect } from 'react';
import { MessageSquare, Mail, Phone, Clock, CheckCircle, Loader2, ChevronLeft, Search, Reply } from 'lucide-react';
import api from '../../utils/api';

const STATUS_STYLE = { unread:'bg-blue-100 text-blue-700', read:'bg-gray-100 text-gray-600', replied:'bg-emerald-100 text-emerald-700' };

export default function AdminContacts() {
  const [items, setItems]   = useState([]);
  const [total, setTotal]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage]     = useState(1);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/contact/admin/all?page=${page}&limit=20${search?`&search=${search}`:''}`);
      setItems(r.data.items||[]); setTotal(r.data.total||0);
    } catch(e){console.error(e);} finally{setLoading(false);}
  };

  useEffect(()=>{fetchItems();},[page,search]);

  const markAs = async (id, status) => {
    await api.put(`/contact/${id}`, { status });
    fetchItems();
    if (selected?._id===id) setSelected(p=>({...p,status}));
  };

  const openMessage = async (item) => {
    setSelected(item);
    if (item.status==='unread') markAs(item._id,'read');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900 flex items-center gap-2"><MessageSquare size={22} className="text-royal-800"/>Messages</h1>
          <p className="text-gray-500 text-sm mt-0.5">{total} contact form submissions</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-card border border-gray-100 overflow-hidden">
        {selected ? (
          <div>
            <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
              <button onClick={()=>setSelected(null)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors"><ChevronLeft size={18}/></button>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">{selected.subject}</h3>
                <p className="text-xs text-gray-400">{selected.name} · {selected.email}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={()=>markAs(selected._id,'read')} className="px-3 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">Mark Read</button>
                <button onClick={()=>markAs(selected._id,'replied')} className="px-3 py-1.5 text-xs font-semibold bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors flex items-center gap-1"><CheckCircle size={12}/>Mark Replied</button>
              </div>
            </div>
            <div className="p-6 max-w-2xl">
              <div className="grid sm:grid-cols-3 gap-4 mb-6">
                {[{icon:Mail,l:'Email',v:selected.email,h:`mailto:${selected.email}`},{icon:Phone,l:'Phone',v:selected.phone||'—'},{icon:Clock,l:'Received',v:new Date(selected.createdAt).toLocaleDateString('en-NG',{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'})}].map(i=>(
                  <div key={i.l} className="bg-gray-50 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 text-xs text-gray-400 font-semibold mb-1"><i.icon size={12}/>{i.l}</div>
                    {i.h ? <a href={i.h} className="text-sm font-medium text-royal-800 hover:underline">{i.v}</a> : <span className="text-sm font-medium text-gray-700">{i.v}</span>}
                  </div>
                ))}
              </div>
              <div className="bg-gray-50 rounded-2xl p-5">
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{selected.message}</p>
              </div>
              <div className="mt-5 flex gap-3">
                <a href={`mailto:${selected.email}?subject=Re: ${selected.subject}`} onClick={()=>markAs(selected._id,'replied')}
                  className="btn-royal text-sm py-2.5 px-5 flex items-center gap-2"><Reply size={15}/>Reply via Email</a>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-100">
              <div className="relative flex-1 max-w-xs">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                <input className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-royal-800/20 transition-all" placeholder="Search messages..." value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}}/>
              </div>
            </div>
            {loading ? (
              <div className="flex items-center justify-center h-48 gap-2 text-gray-400"><Loader2 size={20} className="animate-spin"/>Loading...</div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-gray-400 gap-2"><MessageSquare size={32} className="opacity-30"/><p className="text-sm">No messages yet</p></div>
            ) : (
              <div className="divide-y divide-gray-50">
                {items.map(item=>(
                  <div key={item._id} onClick={()=>openMessage(item)} className={`flex items-start gap-4 px-5 py-4 hover:bg-gray-50 cursor-pointer transition-colors ${item.status==='unread'?'bg-blue-50/40':''}`}>
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${item.status==='unread'?'bg-blue-500':'bg-gray-300'}`}/>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`font-semibold text-sm ${item.status==='unread'?'text-gray-900':'text-gray-700'}`}>{item.name}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_STYLE[item.status]}`}>{item.status}</span>
                      </div>
                      <div className="text-sm text-gray-700 truncate">{item.subject}</div>
                      <div className="text-xs text-gray-400 mt-0.5 truncate">{item.message?.slice(0,80)}...</div>
                    </div>
                    <div className="text-xs text-gray-400 flex-shrink-0">{new Date(item.createdAt).toLocaleDateString()}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
